import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useWorkspaceApi } from "hooks/useWorkspace";
import { useProjectsApi } from "hooks/useProjects";
import { RouteFallback } from "routes/RouteFallback";

/**
 * Component to handle legacy /projects/:id URLs and redirect to workspace-based URLs
 * Determines the workspace from the project and redirects accordingly
 */
export const LegacyProjectRedirect = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { workspaces } = useWorkspaceApi({ take: 50 });
  const { projects } = useProjectsApi({ take: 50 });

  useEffect(() => {
    if (!projectId) return;

    // Add a small delay to ensure data is loaded
    const timeoutId = setTimeout(() => {
      // Find the project to determine its workspace
      const project = projects.find((p) => p.projectId?.toString() === projectId);

      if (project && workspaces.length > 0) {
        // Find the workspace that owns this project
        // First try to find workspace from project's workspaceId if available
        let workspace = workspaces.find((w) => w.id === project.workspaceId);

        // If not found, use the first available workspace as fallback
        if (!workspace) {
          workspace = workspaces[0];
        }

        // Get the current path and redirect to workspace-based URL
        const currentPath = window.location.pathname;
        const pathAfterProject = currentPath.replace(`/projects/${projectId}`, "");

        navigate(`/workspaces/${workspace.id}/projects/${projectId}${pathAfterProject}`, {
          replace: true,
        });
      } else if (workspaces.length > 0) {
        // If project not found but we have workspaces, redirect to first workspace
        const workspace = workspaces[0];
        navigate(`/workspaces/${workspace.id}/projects`, { replace: true });
      } else {
        // No workspaces available, redirect to root
        navigate("/", { replace: true });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [projectId, projects, workspaces, navigate]);

  // Show loading while determining redirect
  return <RouteFallback label="Redirecting to workspace..." />;
};
